import * as React from 'react';
import { Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../state/auth-state';
import { useAuthorization } from '../hooks/use-authorization';
import { Spinner } from '../components/ui/spinner';
import { Badge } from '../components/ui/badge';
import { BreadcrumbProvider, useBreadcrumb } from '../state/breadcrumb-state';
import { Breadcrumb } from '../components/ui/breadcrumb';
import { cn } from '../lib/utils';

/**
 * Authenticated layout guard for the Admin Console.
 * Redirects unauthenticated users to /login (saving the intended URL).
 * Provides the admin shell with sticky header, breadcrumb navigation,
 * and main content area. Displays role badge and admin-specific navigation links.
 *
 * Requirements: 5.1, 5.3, 12.1
 */
export function AuthenticatedLayout(): React.JSX.Element {
  const { state, logout } = useAuth();
  const { isSystemAdmin, role } = useAuthorization();
  const location = useLocation();

  // Show loading spinner during session restoration
  if (state.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-busy="true">
        <Spinner label="Restoring session..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!state.isAuthenticated) {
    sessionStorage.setItem('ddns_admin_return_url', location.pathname);
    return <Navigate to="/admin-console/login" replace />;
  }

  /**
   * Determines if a nav link is active based on the current location.
   * A link is active when the current path starts with the link's href.
   * Special case: Dashboard ("/admin-console") is only active on exact match
   * to avoid it being highlighted on all sub-routes.
   */
  function isNavLinkActive(href: string): boolean {
    if (href === '/admin-console') {
      return location.pathname === '/admin-console' || location.pathname === '/admin-console/';
    }
    return location.pathname.startsWith(href);
  }

  const navLinkBaseClass =
    'rounded-md px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
  const navLinkInactiveClass = 'text-muted-foreground hover:text-foreground';
  const navLinkActiveClass = 'text-foreground font-semibold';

  return (
    <BreadcrumbProvider>
      <div className="min-h-screen bg-background">
        {/* Sticky header */}
        <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-foreground">DelegatedDNS Admin</h1>
              <nav aria-label="Admin navigation">
                <ul className="flex items-center gap-1">
                  <li>
                    <Link
                      to="/admin-console"
                      className={cn(
                        navLinkBaseClass,
                        isNavLinkActive('/admin-console') ? navLinkActiveClass : navLinkInactiveClass,
                      )}
                      aria-current={isNavLinkActive('/admin-console') ? 'page' : undefined}
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/admin-console/domains"
                      className={cn(
                        navLinkBaseClass,
                        isNavLinkActive('/admin-console/domains') ? navLinkActiveClass : navLinkInactiveClass,
                      )}
                      aria-current={isNavLinkActive('/admin-console/domains') ? 'page' : undefined}
                    >
                      Domains
                    </Link>
                  </li>
                  {isSystemAdmin && (
                    <li>
                      <Link
                        to="/admin-console/credentials"
                        className={cn(
                          navLinkBaseClass,
                          isNavLinkActive('/admin-console/credentials') ? navLinkActiveClass : navLinkInactiveClass,
                        )}
                        aria-current={isNavLinkActive('/admin-console/credentials') ? 'page' : undefined}
                      >
                        Credentials
                      </Link>
                    </li>
                  )}
                  {isSystemAdmin && (
                    <li>
                      <Link
                        to="/admin-console/settings"
                        className={cn(
                          navLinkBaseClass,
                          isNavLinkActive('/admin-console/settings') ? navLinkActiveClass : navLinkInactiveClass,
                        )}
                        aria-current={isNavLinkActive('/admin-console/settings') ? 'page' : undefined}
                      >
                        Settings
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={role === 'System_Admin' ? 'default' : 'secondary'}>
                {role}
              </Badge>
              {state.user && (
                <span className="text-sm text-muted-foreground">{state.user.name}</span>
              )}
              <button
                onClick={logout}
                className={cn(navLinkBaseClass, navLinkInactiveClass)}
                aria-label="Sign out"
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        {/* Breadcrumb navigation below header */}
        <BreadcrumbBar />

        {/* Main content */}
        <main className="mx-auto max-w-7xl px-4 py-6">
          <Outlet />
        </main>
      </div>
    </BreadcrumbProvider>
  );
}

/**
 * Internal component that renders the breadcrumb bar.
 * Must be rendered inside BreadcrumbProvider to access the context.
 */
function BreadcrumbBar(): React.JSX.Element | null {
  const { segments } = useBreadcrumb();

  if (segments.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4">
      <Breadcrumb segments={segments} />
    </div>
  );
}
